import React, { useState, useEffect } from "react";
import {
    Box,
    Paper,
    Typography,
    Button,
    Chip,
    Divider,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Pagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tabs,
    Tab,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
// import AddIcon from '@mui/icons-material/Add';
import PlaylistAddOutlinedIcon from "@mui/icons-material/PlaylistAddOutlined";

import {
    CheckCircle,
    Upload,
    Close,
    Layers,
    Edit,
    Delete,
    Visibility,
} from "@mui/icons-material";
import { layer_api_path } from "../api_registry";

export default function LayerManager({ project, openPopup, setOpenPopup ,onNewLayerCallback, onLayerRemovedCallback}) {
    const [layerData, setLayerData] = useState([]);
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [file, setFile] = useState(null);
    const [logList, setLogList] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSetting, setPageSetting] = useState({
        n_pages: 1,
        page_size: 10,
    });
    const [addingLayerId, setAddingLayerId] = useState(null);
    const [deletingLayerId, setDeletingLayerId] = useState(null);
    const [removingLayerId, setRemovingLayerId] = useState(null);

    //  Fetch layers function
    const fetchLayers = async (project_id, page, page_size) => {
        try {
            const response = await fetch(
                `${layer_api_path}/${project_id}/?page_size=${page_size}&page=${page}`,
                { method: "GET", headers: { Accept: "application/json" } }
            );
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();
            return data;
        } catch (err) {
            console.error("Failed to fetch layers:", err);
            return null;
        }
    };

    //  add layer to layer panel
    const addLayerToMap = async (project_id, layer_id) => {
        setAddingLayerId(layer_id);
        try {
            const response = await fetch(
                `${layer_api_path}/${project_id}/add-layer-to-layer-panel`,
                {
                    method: "POST",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json", 
                    },
                    body: JSON.stringify({ layer_id }),
                    credentials: "include",
                }
            );

            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();
            onNewLayerCallback(data);
            
            // Update layer's layer_added status
            setLayerData((prev) =>
                prev.map((layer) =>
                    layer.id === layer_id ? { ...layer, layer_added: true } : layer
                )
            );
            return data;
        } catch (err) {
            console.error("Failed to add layer to map:", err);
        } finally {
            setAddingLayerId(null);
        }
    };

    //  remove layer from layer panel
    const removeLayerFromMap = async (project_id, layer_id) => {
        setRemovingLayerId(layer_id);
        try {
            const response = await fetch(
                `${layer_api_path}/${project_id}/remove-layer-from-layer-panel`,
                {
                    method: "POST",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json", 
                    },
                    body: JSON.stringify({ layer_id }),
                    credentials: "include",
                }
            );

            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();
            
            // Update layer's layer_added status back to false (keep it in list)
            setLayerData((prev) =>
                prev.map((layer) =>
                    layer.id === layer_id ? { ...layer, layer_added: false } : layer
                )
            );
            
            // Notify parent to remove from map panel
            if (onLayerRemovedCallback) {
                onLayerRemovedCallback(layer_id);
            }
            
            return data;
        } catch (err) {
            console.error("Failed to remove layer from map:", err);
        } finally {
            setRemovingLayerId(null);
        }
    };

    //  delete layer
    const deleteLayer = async (project_id, layer_id) => {
        if (!window.confirm("Are you sure you want to delete this layer?")) return;
        
        setDeletingLayerId(layer_id);
        try {
            const response = await fetch(
                `${layer_api_path}/${project_id}/${layer_id}`,
                {
                    method: "DELETE",
                    headers: {
                        Accept: "application/json",
                    },
                    credentials: "include",
                }
            );

            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            
            // Remove layer from list
            setLayerData((prev) => prev.filter((layer) => layer.id !== layer_id));
        } catch (err) {
            console.error("Failed to delete layer:", err);
        } finally {
            setDeletingLayerId(null);
        }
    };

    //  Fetch layers when popup opens or project/page changes
    useEffect(() => {
        if (openPopup && project?.id) {
            (async () => {
                setLoading(true);
                const layerList = await fetchLayers(
                    project.id,
                    page,
                    pageSetting.page_size
                );
                if (layerList) {
                    const n_pages = Math.ceil(
                        layerList.total / pageSetting.page_size
                    );
                    setPageSetting((prev) => ({ ...prev, n_pages }));
                    setLayerData(layerList.items);
                }
                setLoading(false);
            })();
        }
    }, [openPopup, project?.id, page]);

    const getStatusChip = (status) => {
        const map = {
            Success: { color: "success", label: "Success" },
            Processing: { color: "warning", label: "Processing" },
            Failed: { color: "error", label: "Failed" },
        };
        const cfg = map[status] || map.Success;
        return <Chip label={cfg.label} color={cfg.color} size="small" />;
    };

    const handleFile = (e) => {
        setFile(e.target.files[0]);
        e.target.value = null;
    };

    //  Upload handler with log streaming
    const handleUpload = async () => {
        if (!file || !project?.id) return;

        const formData = new FormData();
        formData.append("file", file);

        const fileName = file.name;
        const fileSize = (file.size / 1024).toFixed(2) + " KB";
        const startTime = new Date();
        let uploadLog = {
            fileName,
            fileSize,
            steps: [
                `File name: ${fileName}`,
                `File size: ${fileSize}`,
                `Uploading started at: ${startTime.toLocaleString()}`,
                "",
            ],
            status: null,
        };
        setLogList((prev) => [uploadLog, ...prev]);

        setLoading(true);
        try {
            const response = await fetch(`${layer_api_path}/${project.id}/`, {
                method: "POST",
                headers: { accept: "application/json" },
                body: formData,
                mode: "cors",
                credentials: "include",
            });
            if (!response.ok)
                throw new Error(`Upload failed: ${response.status}`);

            const reader = response.body.getReader();
            const decoder = new TextDecoder("utf-8");
            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                const chunk = decoder.decode(value, { stream: true });
                uploadLog.steps = [...uploadLog.steps, chunk];
                setLogList((prev) => [{ ...uploadLog }, ...prev.slice(1)]);
            }

            uploadLog.status = "Success";
            setLogList((prev) => [{ ...uploadLog }, ...prev.slice(1)]);
        } catch (err) {
            const errorMsg = err.message || "Unknown error occurred";
            console.error("Upload failed:", err);
            uploadLog.steps = [
                ...uploadLog.steps,
                "",
                `ERROR: ${errorMsg}`,
                "Upload failed. Please try again or contact support.",
            ];
            uploadLog.status = "Failed";
            setLogList((prev) => [{ ...uploadLog }, ...prev.slice(1)]);
        } finally {
            //  Refresh layer list after upload
            const layerList = await fetchLayers(
                project.id,
                page,
                pageSetting.page_size
            );
            if (layerList) {
                const n_pages = Math.ceil(
                    layerList.total / pageSetting.page_size
                );
                setPageSetting((prev) => ({ ...prev, n_pages }));
                setLayerData(layerList.items);
            }
            setFile(null);
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={openPopup}
            onClose={() => setOpenPopup(false)}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: 1,
                    borderColor: "divider",
                    pb: 1,
                }}
            >
                <Typography variant="h6" component="div" fontWeight={600}>
                    Manage Map Layers
                </Typography>
                <Button
                    onClick={() => setOpenPopup(false)}
                    sx={{ color: "text.secondary" }}
                >
                    <Close />
                </Button>
            </DialogTitle>

            <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
                    <Tab label="Upload Layer" sx={{ textTransform: "none" }} />
                    <Tab
                        label={`Manage Layers (${layerData.length})`}
                        sx={{ textTransform: "none" }}
                    />
                </Tabs>
            </Box>

            <DialogContent
                sx={{
                    pt: 3,
                    pb: 3,
                    display: "flex",
                    flexDirection: "column",
                    height: "70vh",
                    overflow: "hidden",
                }}
            >
                {activeTab === 0 && (
                    <>
                        {/* Upload Section */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "stretch",
                                mb: 1,
                                mt: 0,
                                flexShrink: 0,
                                border: 1,
                                borderColor: "divider",
                                borderRadius: 1,
                                overflow: "hidden",
                            }}
                        >
                            <Button
                                variant="contained"
                                component="label"
                                sx={{ borderRadius: 0, px: 3 }}
                            >
                                Choose File
                                <input
                                    hidden
                                    type="file"
                                    accept=".kml"
                                    onChange={handleFile}
                                />
                            </Button>
                            <Box
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    px: 2,
                                    bgcolor: "white",
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    color={
                                        file ? "text.primary" : "text.secondary"
                                    }
                                >
                                    {file?.name || "No file chosen"}
                                </Typography>
                            </Box>
                            <Button
                                variant="outlined"
                                onClick={handleUpload}
                                disabled={!file || loading}
                                startIcon={
                                    loading ? (
                                        <CircularProgress
                                            size={18}
                                            color="inherit"
                                        />
                                    ) : (
                                        <Upload />
                                    )
                                }
                                sx={{
                                    borderRadius: 0,
                                    px: 3,
                                    borderLeft: 1,
                                    borderColor: "divider",
                                }}
                            >
                                Upload
                            </Button>
                        </Box>

                        {/* Upload Log Section */}
                        <Paper
                            variant="outlined"
                            sx={{
                                overflow: "hidden",
                                display: "flex",
                                flexDirection: "column",
                                flex: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    bgcolor: "primary.main",
                                    color: "white",
                                    p: 2,
                                }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    fontWeight={600}
                                >
                                    Upload History
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{ color: "primary.light" }}
                                >
                                    Total uploads: {logList.length}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    p: 2,
                                    flex: 1,
                                    overflowY: "auto",
                                    bgcolor: "white",
                                }}
                            >
                                {logList.length === 0 ? (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            height: 250,
                                            color: "text.disabled",
                                        }}
                                    >
                                        <Upload
                                            sx={{
                                                fontSize: 48,
                                                mb: 1.5,
                                                opacity: 0.3,
                                            }}
                                        />
                                        <Typography variant="body2">
                                            No files uploaded yet
                                        </Typography>
                                    </Box>
                                ) : (
                                    logList.map((item, idx) => (
                                        <Box key={idx}>
                                            <Box sx={{ py: 1.5 }}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1,
                                                        mb: 1,
                                                    }}
                                                >
                                                    <CheckCircle
                                                        sx={{
                                                            fontSize: 20,
                                                            color: item.status
                                                                ? "success.main"
                                                                : "grey.400",
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={500}
                                                    >
                                                        {item.fileName}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        ({item.fileSize})
                                                    </Typography>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        ml: 3.5,
                                                        fontFamily:
                                                            "Consolas, monospace",
                                                        bgcolor: "#f5f5f5",
                                                        p: 2,
                                                        borderRadius: 1,
                                                        fontSize: "0.75rem",
                                                        border: "1px solid #e0e0e0",
                                                    }}
                                                >
                                                    {item.steps.map(
                                                        (s, sIdx) => (
                                                            <Box
                                                                key={sIdx}
                                                                sx={{
                                                                    color: s.includes(
                                                                        "finished"
                                                                    )
                                                                        ? "success.main"
                                                                        : "text.primary",
                                                                    mb: 0.3,
                                                                    lineHeight: 1.5,
                                                                }}
                                                            >
                                                                {s || "\u00A0"}
                                                            </Box>
                                                        )
                                                    )}
                                                </Box>
                                            </Box>
                                            {idx < logList.length - 1 && (
                                                <Divider />
                                            )}
                                        </Box>
                                    ))
                                )}
                            </Box>
                        </Paper>
                    </>
                )}

                {/* Manage Layers */}
                {activeTab === 1 && (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            flex: 1,
                            minHeight: 0,
                        }}
                    >
                        {loading ? (
                            <Box
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        ) : (
                            <>
                                <TableContainer
                                    component={Paper}
                                    variant="outlined"
                                >
                                    <Table stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell align="center">
                                                    ID
                                                </TableCell>
                                                <TableCell align="center">
                                                    Name
                                                </TableCell>
                                                <TableCell align="center">
                                                    Geometry Type
                                                </TableCell>
                                                <TableCell align="center">
                                                    Status
                                                </TableCell>
                                                <TableCell align="center">
                                                    Actions
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {layerData.length === 0 ? (
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={5}
                                                        align="center"
                                                        sx={{ py: 8 }}
                                                    >
                                                        <Layers
                                                            sx={{
                                                                fontSize: 48,
                                                                opacity: 0.3,
                                                                color: "text.disabled",
                                                            }}
                                                        />
                                                        <Typography
                                                            variant="body2"
                                                            color="text.disabled"
                                                        >
                                                            No layers available
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                layerData.map((layer) => (
                                                    <TableRow
                                                        key={layer.id}
                                                        hover
                                                    >
                                                        <TableCell align="center">
                                                            {layer.id}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            {layer.name}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            {
                                                                layer.geometry_types
                                                            }
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            {getStatusChip(
                                                                layer.status
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box
                                                                sx={{
                                                                    display:
                                                                        "flex",
                                                                    gap: 1,
                                                                    justifyContent:
                                                                        "center",
                                                                }}
                                                            >
                                                                {/* Delete Button */}
                                                                <Button
                                                                    variant="outlined"
                                                                    color="error"
                                                                    disabled={
                                                                        deletingLayerId === layer.id
                                                                    }
                                                                    startIcon={
                                                                        deletingLayerId === layer.id ? (
                                                                            <CircularProgress
                                                                                size={16}
                                                                                color="inherit"
                                                                            />
                                                                        ) : (
                                                                            <Delete />
                                                                        )
                                                                    }
                                                                    size="small"
                                                                    onClick={() =>
                                                                        deleteLayer(
                                                                            project.id,
                                                                            layer.id
                                                                        )
                                                                    }
                                                                    sx={{
                                                                        textTransform:
                                                                            "none",
                                                                        fontSize:
                                                                            "0.8rem",
                                                                        borderRadius: 2,
                                                                        px: 1.5,
                                                                        py: 0.5,
                                                                        minWidth:
                                                                            "auto",
                                                                        borderColor:
                                                                            "rgba(211, 47, 47, 0.4)",
                                                                        "&:hover":
                                                                            {
                                                                                backgroundColor:
                                                                                    "rgba(211, 47, 47, 0.08)",
                                                                                borderColor:
                                                                                    "rgba(211, 47, 47, 0.8)",
                                                                            },
                                                                    }}
                                                                >
                                                                    Delete
                                                                </Button>

                                                                {/* Add/Remove from Map Button */}
                                                                <Button
                                                                    variant="contained"
                                                                    size="small"
                                                                    color={
                                                                        layer.layer_added
                                                                            ? "error"
                                                                            : "primary"
                                                                    }
                                                                    disabled={
                                                                        addingLayerId ===
                                                                            layer.id ||
                                                                        removingLayerId ===
                                                                            layer.id
                                                                    }
                                                                    startIcon={
                                                                        addingLayerId ===
                                                                            layer.id ||
                                                                        removingLayerId ===
                                                                            layer.id ? (
                                                                            <CircularProgress
                                                                                size={16}
                                                                                color="inherit"
                                                                            />
                                                                        ) : (
                                                                            <PlaylistAddOutlinedIcon />
                                                                        )
                                                                    }
                                                                    onClick={() =>
                                                                        layer.layer_added
                                                                            ? removeLayerFromMap(
                                                                                project.id,
                                                                                layer.id
                                                                            )
                                                                            : addLayerToMap(
                                                                                project.id,
                                                                                layer.id
                                                                            )
                                                                    }
                                                                    sx={{
                                                                        textTransform:
                                                                            "none",
                                                                        fontSize:
                                                                            "0.8rem",
                                                                        borderRadius: 2,
                                                                        px: 1.5,
                                                                        py: 0.5,
                                                                        minWidth:
                                                                            "auto",
                                                                        boxShadow:
                                                                            "none",
                                                                        "&:hover":
                                                                            {
                                                                                boxShadow:
                                                                                    "none",
                                                                                opacity: 0.9,
                                                                            },
                                                                    }}
                                                                >
                                                                    {addingLayerId ===
                                                                    layer.id
                                                                        ? "Adding..."
                                                                        : removingLayerId ===
                                                                          layer.id
                                                                        ? "Removing..."
                                                                        : layer.layer_added
                                                                        ? "Remove from Map"
                                                                        : "Add to Map"}
                                                                </Button>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {layerData.length > 0 && (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            mt: 2,
                                            pt: 2,
                                        }}
                                    >
                                        <Pagination
                                            count={pageSetting.n_pages}
                                            page={page}
                                            onChange={(e, v) => setPage(v)}
                                            color="primary"
                                            showFirstButton
                                            showLastButton
                                        />
                                    </Box>
                                )}
                            </>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    px: 3,
                    py: 2,
                    bgcolor: "grey.50",
                    borderTop: 1,
                    borderColor: "divider",
                }}
            >
                <Button onClick={() => setOpenPopup(false)} variant="outlined">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}
